/**
 * 運用詳細画面 タブ定数・表示設定定義
 *
 * タブキー/ラベル、見える化データ・メンテ情報の型と
 * APIレスポンスから表示データへの変換に使用する設定を管理する。
 */

// ========== 型定義 ==========

/** 見える化レジスタ表示項目 */
export type RegisterDisplayItem = {
  readonly name: string;
  readonly value: number | null;
  readonly unit: string;
  readonly precision: number;
};

/** メンテナンス動作回数表示項目 */
export type MaintenanceDisplayItem = {
  readonly name: string;
  readonly value: number | null;
  readonly unit: string;
};

// ========== タブ定数 ==========

export const PANEL_DETAIL_TAB_KEYS = {
  BASIC_INFO: 'basic-info',
  VISUALIZATION_DATA: 'visualization-data',
  MAINTENANCE: 'maintenance',
  ERROR: 'error',
} as const;

export type PanelDetailTabKey = typeof PANEL_DETAIL_TAB_KEYS[keyof typeof PANEL_DETAIL_TAB_KEYS];

export const PANEL_DETAIL_TAB_LABELS: Record<PanelDetailTabKey, string> = {
  [PANEL_DETAIL_TAB_KEYS.BASIC_INFO]: '基本情報',
  [PANEL_DETAIL_TAB_KEYS.VISUALIZATION_DATA]: '見える化データ',
  [PANEL_DETAIL_TAB_KEYS.MAINTENANCE]: 'メンテ',
  [PANEL_DETAIL_TAB_KEYS.ERROR]: 'エラー',
} as const;

// ========== 見える化センサー表示設定 ==========

/** 見える化センサーフィールドの表示設定 */
export type VisualizationSensorDisplayConfigItem = {
  readonly fieldName: string;
  readonly displayName: string;
  readonly unit: string;
  readonly precision: number;
  readonly divisor: number;
};

/** 見える化データのスケーリング除数 */
const VISUALIZATION_DIVISOR_NONE = 1;
const VISUALIZATION_DIVISOR_STANDARD = 10;
const VISUALIZATION_DIVISOR_PRESSURE = 1000;

export const VISUALIZATION_SENSOR_DISPLAY_CONFIG: readonly VisualizationSensorDisplayConfigItem[] = [
  { fieldName: 'lAfPressureFlowSensorFlow',                displayName: 'L-AF圧力流量センサー/エアー流量',                  unit: 'L/min', precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
  { fieldName: 'lAfPressureFlowSensorPressure',             displayName: 'L-AF圧力流量センサー/エアー圧力',                  unit: 'MPa',   precision: 1, divisor: VISUALIZATION_DIVISOR_PRESSURE },
  { fieldName: 'lAfPressureFlowSensorTemperature',          displayName: 'L-AF圧力流量センサー/温度',                        unit: '℃',    precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
  { fieldName: 'lAfTankLevelSensorDistance',                displayName: 'L-AFタンク液面センサー/センサーから対象物までの距離', unit: 'mm',    precision: 0, divisor: VISUALIZATION_DIVISOR_NONE },
  { fieldName: 'lAfFlowSensorFlow',                         displayName: 'L-AF流量センサー/流量',                            unit: 'L/min', precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
  { fieldName: 'lAfFlowSensorPipeSurfaceTemperature',       displayName: 'L-AF流量センサー/配管表面温度',                     unit: '℃',    precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
  { fieldName: 'hAfPressureSensorPressure',                 displayName: 'Ｈ-AF圧力センサー/圧力',                           unit: 'MPa',   precision: 1, divisor: VISUALIZATION_DIVISOR_PRESSURE },
  { fieldName: 'hAfPressureSensorTemperature',              displayName: 'Ｈ-AF圧力センサー/温度',                           unit: '℃',    precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
  { fieldName: 'concretePumpFeedAmount',                    displayName: 'コンクリートポンプ送り量',                          unit: '㎥/h',  precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
  { fieldName: 'lAfTankTemperatureSensorLiquidTemperature', displayName: 'L-AFタンク温度センサー/Ｌ－ＡＦ液体温度',           unit: '℃',    precision: 1, divisor: VISUALIZATION_DIVISOR_STANDARD },
] as const;

// ========== メンテナンス動作回数表示設定 ==========

/** メンテナンス動作回数フィールドの表示設定 */
export type MaintenanceOperationDisplayConfigItem = {
  readonly fieldName: string;
  readonly displayName: string;
  readonly unit: string;
};

export const MAINTENANCE_OPERATION_DISPLAY_CONFIG: readonly MaintenanceOperationDisplayConfigItem[] = [
  { fieldName: 'lAfPumpOperationCount',         displayName: 'L-AFポンプ動作回数',       unit: '回' },
  { fieldName: 'hAfOperationCount',             displayName: 'H-AF動作回数',             unit: '回' },
  { fieldName: 'barashiAirValveOperationCount', displayName: 'バラシエアー弁動作回数',   unit: '回' },
  { fieldName: 'lAfTransferPumpOperationCount', displayName: 'L-AF移送ポンプ動作回数',   unit: '回' },
] as const;

// ========== エラーフラグ表示設定 ==========

/** エラーフラグ表示項目 */
export type ErrorDisplayItem = {
  readonly name: string;
  readonly isActive: boolean;
  readonly errorCode: number | null;
};

/** エラーフラグフィールドの表示設定 */
export type ErrorFlagDisplayConfigItem = {
  readonly fieldName: string;
  readonly displayName: string;
  readonly errorCodeFieldName: string | null;
};

export const ERROR_FLAG_DISPLAY_CONFIG: readonly ErrorFlagDisplayConfigItem[] = [
  { fieldName: 'lAfPumpError',                 displayName: 'L-AFポンプ異常',             errorCodeFieldName: null },
  /**
   * 【表記方針】LSA→L-AF表記変更。詳細はバックエンド定数を参照。
   * @see faro-api/src/constants/error-flags.ts ERROR_FLAG_DISPLAY_NAMES
   */
  { fieldName: 'lsaFlowDecrease',              displayName: 'L-AF流量低下',                errorCodeFieldName: null },
  { fieldName: 'lsaTankLevelLow',              displayName: 'L-AFタンクレベルL',            errorCodeFieldName: null },
  { fieldName: 'lsaTankLevelVeryLow',          displayName: 'L-AFタンクレベルLL',           errorCodeFieldName: null },
  { fieldName: 'transferPumpInv1Status',       displayName: '移送ポンプ(INV1)異常',       errorCodeFieldName: 'transferPumpInv1ErrorCode' },
  { fieldName: 'lAfPumpInv2Status',            displayName: 'L-AFポンプ(INV2)異常',       errorCodeFieldName: 'lAfPumpInv2ErrorCode' },
  { fieldName: 'lAfTemperatureError',          displayName: 'L-AFタンク温度異常',           errorCodeFieldName: null },
  { fieldName: 'sequencerBatteryVoltageDrop',  displayName: 'シーケンサバッテリ電圧低下',   errorCodeFieldName: null },
  { fieldName: 'touchPanelBatteryVoltageDrop', displayName: 'タッチパネルバッテリ電圧低下', errorCodeFieldName: null },
  // 【例外的処理】メンテバッファ由来のエラーフラグ（maintenanceInfoBuffer3/4 から取得）
  { fieldName: 'transferPumpFailure',          displayName: '移送ポンプ故障',             errorCodeFieldName: null },
  { fieldName: 'transferPumpDryRunning',       displayName: '移送ポンプ空運転',            errorCodeFieldName: null },
] as const;
